import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, eventPackageService } from '../services/api';
import type { EventPackageAdminDto, EventPackageItemDto } from '../types';
import '../styles/AdminNews.css';
import '../styles/AdminEventPackages.css';

interface PackageForm {
    id: number | null;
    name: string;
    description: string;
    isActive: boolean;
    sortOrder: number;
    code: string;
    items: EventPackageItemDto[];
}

const emptyForm: PackageForm = {
    id: null,
    name: '',
    description: '',
    isActive: true,
    sortOrder: 0,
    code: '',
    items: [],
};

const AdminEventPackages: React.FC = () => {
    const navigate = useNavigate();
    const [packages, setPackages] = useState<EventPackageAdminDto[]>([]);
    const [selected, setSelected] = useState<EventPackageAdminDto | null>(null);
    const [form, setForm] = useState<PackageForm>(emptyForm);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        const checkAccessAndLoad = async () => {
            if (!authService.isAuthenticated()) {
                navigate('/login');
                return;
            }
            if (!(await authService.checkIsAdmin())) {
                navigate('/');
                return;
            }
            await load();
        };
        checkAccessAndLoad();
    }, [navigate]);

    const load = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await eventPackageService.listPackages();
            setPackages(data);
        } catch (err) {
            console.error('Erro ao carregar pacotes', err);
            setError('Não foi possível carregar os pacotes de evento.');
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (item: EventPackageAdminDto | null) => {
        setSelected(item);
        setSuccess(null);
        if (item) {
            setForm({
                id: item.id,
                name: item.name,
                description: item.description ?? '',
                isActive: item.isActive,
                sortOrder: item.sortOrder,
                code: item.code ?? '',
                items: (item.items ?? []).map((i) => ({
                    recId: i.recId,
                    className: i.className ?? '',
                    itemName: i.itemName ?? '',
                    amount: i.amount ?? 1,
                    period: i.period ?? 0,
                })),
            });
        } else {
            setForm(emptyForm);
        }
    };

    const addItem = () => {
        setForm((prev) => ({
            ...prev,
            items: [...prev.items, { recId: '', className: '', itemName: '', amount: 1, period: 0 }],
        }));
    };

    const removeItem = (index: number) => {
        setForm((prev) => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index),
        }));
    };

    const updateItem = (index: number, field: keyof EventPackageItemDto, value: string | number) => {
        setForm((prev) => ({
            ...prev,
            items: prev.items.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
        }));
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const target = e.target;
        const { name, value } = target;
        if (target instanceof HTMLInputElement && target.type === 'checkbox') {
            setForm((prev) => ({ ...prev, [name]: target.checked }));
        } else if (name === 'sortOrder') {
            setForm((prev) => ({ ...prev, [name]: Number(value) }));
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        setSuccess(null);
        const payload = {
            name: form.name,
            description: form.description || null,
            isActive: form.isActive,
            sortOrder: form.sortOrder,
            code: form.code || null,
            items: form.items
                .filter((i) => i.recId.trim())
                .map((i) => ({
                    recId: i.recId.trim(),
                    className: i.className?.trim() || null,
                    itemName: i.itemName?.trim() || null,
                    amount: Math.max(1, Number(i.amount) || 1),
                    period: Number(i.period) || 0,
                })),
        };
        try {
            let createCode: string | null = null;
            if (form.id) {
                await eventPackageService.updatePackage(form.id, payload);
            } else {
                const res = await eventPackageService.createPackage(payload);
                createCode = res.code ?? null;
            }
            await load();
            handleSelect(null);
            setSuccess(createCode ? `Pacote criado. Código ativo: ${createCode}` : 'Pacote salvo com sucesso.');
        } catch (err) {
            console.error('Erro ao salvar pacote', err);
            setError('Não foi possível salvar o pacote.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (item: EventPackageAdminDto) => {
        if (!window.confirm(`Excluir o pacote "${item.name}"? Esta ação não pode ser desfeita.`)) return;
        try {
            await eventPackageService.deletePackage(item.id);
            await load();
            if (selected?.id === item.id) handleSelect(null);
        } catch (err) {
            console.error('Erro ao excluir pacote', err);
            setError('Não foi possível excluir o pacote.');
        }
    };

    const handleGenerateCode = async (item: EventPackageAdminDto) => {
        if (!window.confirm(`Gerar um novo código para "${item.name}"?\n\nO código atual deixará de funcionar.`)) return;
        try {
            const res = await eventPackageService.regenerateCode(item.id);
            setSuccess(`Novo código ativo: ${res.code}`);
            await load();
        } catch (err) {
            console.error('Erro ao gerar código', err);
            setError('Não foi possível gerar o código.');
        }
    };

    return (
        <div className="admin-page admin-panel-content">
            <h1 className="admin-page-title">Pacotes de Evento</h1>
            <p className="admin-page-subtitle">
                Crie pacotes com itens entregues no Elora Storage (Kind='ev', RecId em minúsculas) e gere códigos
                promocionais. Cada conta pode resgatar somente 1 pacote.
            </p>

            {error && <div className="admin-error">{error}</div>}
            {success && <div className="admin-success">{success}</div>}

            <div className="admin-grid">
                <div className="admin-list">
                    <div className="admin-list-header">
                        <h2>Pacotes</h2>
                        <button type="button" className="btn btn-secondary" onClick={() => handleSelect(null)}>
                            Novo pacote
                        </button>
                    </div>

                    {loading ? (
                        <div className="admin-loading">Carregando pacotes...</div>
                    ) : (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Ativo</th>
                                    <th>Código</th>
                                    <th>Resgates</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {packages.map((item) => (
                                    <tr key={item.id} className={selected?.id === item.id ? 'selected' : ''}>
                                        <td>
                                            <button
                                                type="button"
                                                className="cell-link"
                                                onClick={() => handleSelect(item)}
                                            >
                                                {item.name}
                                            </button>
                                        </td>
                                        <td>{item.isActive ? 'Sim' : 'Não'}</td>
                                        <td>
                                            <span className="pkg-code">{item.code ?? '—'}</span>
                                        </td>
                                        <td>{item.redemptions}</td>
                                        <td>
                                            <div className="row-actions">
                                                <button
                                                    type="button"
                                                    className="btn btn-small"
                                                    onClick={() => handleGenerateCode(item)}
                                                    disabled={!item.isActive}
                                                    title="Gerar novo código (invalida o atual)"
                                                >
                                                    <i className="ri-refresh-line"></i> Código
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-small btn-danger"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(item);
                                                    }}
                                                >
                                                    Excluir
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {packages.length === 0 && !loading && (
                                    <tr><td colSpan={5}>Nenhum pacote cadastrado.</td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="admin-form-wrapper">
                    <h2>{form.id ? 'Editar pacote' : 'Novo pacote'}</h2>
                    <form className="admin-form" onSubmit={handleSubmit}>
                        <div className="form-row">
                            <label htmlFor="name">Nome do pacote</label>
                            <input id="name" name="name" type="text" value={form.name} onChange={handleChange} required />
                        </div>

                        <div className="form-row">
                            <label htmlFor="description">Descrição (aparece na Home)</label>
                            <textarea
                                id="description"
                                name="description"
                                value={form.description}
                                onChange={handleChange}
                                rows={2}
                            />
                        </div>

                        <div className="form-row-inline">
                            <div className="form-group">
                                <label htmlFor="sortOrder">Ordem</label>
                                <input
                                    id="sortOrder"
                                    name="sortOrder"
                                    type="number"
                                    value={form.sortOrder}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="code">Código promocional</label>
                                <input
                                    id="code"
                                    name="code"
                                    type="text"
                                    value={form.code}
                                    onChange={handleChange}
                                    placeholder="Ex.: EVENTO-2026 (hífens/espaço ignorados; letras irrelevantes)"
                                />
                            </div>
                        </div>

                        <div className="form-row-checkbox">
                            <label>
                                <input
                                    type="checkbox"
                                    name="isActive"
                                    checked={form.isActive}
                                    onChange={handleChange}
                                />
                                Pacote ativo (disponível para resgate)
                            </label>
                        </div>

                        <div className="form-row">
                            <label>Itens do pacote (entregues no Elora Storage)</label>
                            <div className="pkg-items">
                                <div className="pkg-items-head">
                                    <span>RecId (minúsculo)</span>
                                    <span>Classe (opcional)</span>
                                    <span>Nome</span>
                                    <span>Qtd</span>
                                    <span>Período (min)</span>
                                    <span></span>
                                </div>
                                {form.items.map((item, index) => (
                                    <div key={index} className="pkg-item-row">
                                        <input
                                            type="text"
                                            value={item.recId}
                                            onChange={(e) => updateItem(index, 'recId', e.target.value)}
                                            placeholder="kn_ev_bx_00_0000"
                                        />
                                        <input
                                            type="text"
                                            value={item.className ?? ''}
                                            onChange={(e) => updateItem(index, 'className', e.target.value)}
                                            placeholder="Guardian (ou deixe vazio)"
                                        />
                                        <input
                                            type="text"
                                            value={item.itemName ?? ''}
                                            onChange={(e) => updateItem(index, 'itemName', e.target.value)}
                                            placeholder="Caixa de Evento"
                                        />
                                        <input
                                            type="number"
                                            min={1}
                                            value={item.amount ?? 1}
                                            onChange={(e) => updateItem(index, 'amount', Number(e.target.value))}
                                        />
                                        <input
                                            type="number"
                                            min={0}
                                            value={item.period ?? 0}
                                            onChange={(e) => updateItem(index, 'period', Number(e.target.value))}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-small btn-danger"
                                            onClick={() => removeItem(index)}
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </button>
                                    </div>
                                ))}
                                {form.items.length === 0 && (
                                    <p className="pkg-items-empty">Nenhum item adicionado ainda.</p>
                                )}
                                <button type="button" className="btn btn-secondary btn-small" onClick={addItem}>
                                    <i className="ri-add-line"></i> Adicionar item
                                </button>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? 'Salvando...' : 'Salvar pacote'}
                            </button>
                            {form.id && (
                                <button type="button" className="btn btn-secondary" onClick={() => handleSelect(null)}>
                                    Limpar formulário
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AdminEventPackages;